import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { 
  ArrowRight, 
  Play, 
  Target, 
  Crown, 
  Star,
  Factory,
  Cog,
  Zap,
  Shield,
  Award,
  Sparkles
} from "lucide-react";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Frame count for the animation sequence
const FRAME_COUNT = 159; // frames 0-158

// Generate frame URLs
const getFrameUrl = (index) => {
  const paddedIndex = String(index).padStart(3, '0');
  return `https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/frame_${paddedIndex}_delay-0.04s.webp`;
};

// --- TRUSTED CLIENTS/INDUSTRIES ---
const CLIENTS = [
  { name: "Automotive", icon: Cog },
  { name: "Aerospace", icon: Zap },
  { name: "Industrial", icon: Factory },
  { name: "Defense", icon: Shield },
  { name: "Electronics", icon: Sparkles },
  { name: "Manufacturing", icon: Award },
];

// --- SUB-COMPONENTS ---
const StatItem = ({ value, label }) => (
  <div className="flex flex-col items-center justify-center transition-transform hover:-translate-y-1 cursor-default">
    <span className="text-xl font-bold text-white sm:text-2xl">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium sm:text-xs">{label}</span>
  </div>
);

// --- MAIN COMPONENT ---
export default function GlassmorphismTrustHero() {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const loadedImagesRef = useRef(new Set());
  const scrollTriggerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;

    // Set canvas size
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Preload images
    const images = [];

    const renderFrame = (index) => {
      try {
        const img = imagesRef.current[index];
        const loadedImages = loadedImagesRef.current;
        // Only draw if image exists, is loaded, and has valid dimensions
        if (img && loadedImages.has(index) && img.naturalWidth > 0 && img.naturalHeight > 0) {
          context.clearRect(0, 0, canvas.width, canvas.height);
          
          // Calculate cover sizing
          const imgRatio = img.naturalWidth / img.naturalHeight;
          const canvasRatio = canvas.width / canvas.height;
          
          let drawWidth, drawHeight, drawX, drawY;
          
          if (canvasRatio > imgRatio) {
            drawWidth = canvas.width;
            drawHeight = canvas.width / imgRatio;
            drawX = 0;
            drawY = (canvas.height - drawHeight) / 2;
          } else {
            drawHeight = canvas.height;
            drawWidth = canvas.height * imgRatio;
            drawX = (canvas.width - drawWidth) / 2;
            drawY = 0;
          }
          
          context.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        }
      } catch (e) {
        // Silently handle draw errors for broken images
      }
    };

    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      const frameIndex = i;
      img.onload = () => {
        loadedImagesRef.current.add(frameIndex);
        if (frameIndex === 0) {
          renderFrame(0);
        }
      };
      img.onerror = () => {
        // Mark as broken so we skip it
      };
      img.src = getFrameUrl(i);
      images.push(img);
    }
    imagesRef.current = images;

    // GSAP ScrollTrigger animation with pinning
    const ctx = gsap.context(() => {
      scrollTriggerRef.current = ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: "top top",
        end: "+=200%",
        scrub: 0.5,
        pin: containerRef.current,
        pinSpacing: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          const frameIndex = Math.floor(self.progress * (FRAME_COUNT - 1));
          renderFrame(frameIndex);
        }
      });
    }, wrapperRef);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      ctx.revert(); // Clean up GSAP context properly
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div ref={containerRef} className="relative w-full bg-zinc-950 text-white overflow-hidden font-sans min-h-screen">
      {/* 
        SCOPED ANIMATIONS 
      */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-fade-in {
          animation: fadeSlideIn 0.8s ease-out forwards;
          opacity: 0;
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
      `}</style>

      {/* Frame Animation Canvas Background */}
      <canvas 
        ref={canvasRef}
        className="absolute inset-0 z-0 w-full h-full opacity-50"
        style={{
          maskImage: "linear-gradient(180deg, transparent 0%, black 5%, black 70%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(180deg, transparent 0%, black 5%, black 70%, transparent 100%)",
        }}
      />
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-zinc-950/60 via-zinc-950/40 to-zinc-950/80" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-6 md:pt-44 md:pb-32 lg:px-8 min-h-screen flex items-center">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start w-full">
          
          {/* --- LEFT COLUMN --- */}
          <div className="lg:col-span-7 flex flex-col justify-center space-y-8 pt-8">
            
            {/* Badge */}
            <div className="animate-fade-in delay-100">
              <div className="inline-flex items-center gap-2 rounded-full border border-zinc-700/50 bg-zinc-800/50 px-3 py-1.5 backdrop-blur-md transition-colors hover:bg-zinc-700/50">
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                  ISO 9001:2015 Certified
                  <Star className="w-3.5 h-3.5 text-zinc-400 fill-zinc-500" />
                </span>
              </div>
            </div>

            {/* Heading */}
            <h1 
              className="animate-fade-in delay-200 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium tracking-tighter leading-[0.9] text-zinc-200"
            >
              Precision Laser<br />
              <span className="bg-gradient-to-br from-zinc-300 via-zinc-400 to-zinc-500 bg-clip-text text-transparent">
                Cutting
              </span><br />
              Excellence
            </h1>

            {/* Description */}
            <p className="animate-fade-in delay-300 max-w-xl text-lg text-zinc-400 leading-relaxed">
              We deliver cutting-edge laser cutting and metal fabrication solutions,
              combining precision engineering with innovative technology to bring your designs to life.
            </p>

            {/* CTA Buttons */}
            <div className="animate-fade-in delay-400 flex flex-col sm:flex-row gap-4">
              <Link 
                to="/services"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-zinc-950 transition-all hover:scale-[1.02] hover:bg-zinc-200 active:scale-[0.98]"
              >
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              
              <Link 
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/10 hover:border-white/20"
              >
                <Play className="w-4 h-4 fill-current" />
                Get a Quote
              </Link>
            </div>
          </div>

          {/* --- RIGHT COLUMN --- */}
          <div className="lg:col-span-5 space-y-6 lg:mt-12">
            
            {/* Stats Card */}
            <div className="animate-fade-in delay-500 relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl shadow-2xl">
              {/* Card Glow Effect */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 h-64 w-64 rounded-full bg-white/5 blur-3xl pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-bold tracking-tight text-white">1000+</div>
                    <div className="text-sm text-zinc-400">Projects Delivered</div>
                  </div>
                </div>

                {/* Progress Bar Section */}
                <div className="space-y-3 mb-8">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Client Satisfaction</span>
                    <span className="text-white font-medium">95%</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-800/50">
                    <div className="h-full w-[95%] rounded-full bg-gradient-to-r from-white to-zinc-400" />
                  </div>
                </div>

                <div className="h-px w-full bg-white/10 mb-6" />

                {/* Mini Stats Grid */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <StatItem value="3+" label="Years" />
                  <StatItem value="100+" label="Clients" />
                  <StatItem value="100%" label="Quality" />
                </div>

                {/* Tag Pills */}
                <div className="mt-8 flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-wide text-zinc-300">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    ACTIVE
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-medium tracking-wide text-zinc-300">
                    <Crown className="w-3 h-3 text-yellow-500" />
                    ISO CERTIFIED
                  </div>
                </div>
              </div>
            </div>

            {/* Marquee Card */}
            <div className="animate-fade-in delay-500 relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 py-8 backdrop-blur-xl">
              <h3 className="mb-6 px-8 text-sm font-medium text-zinc-400">Trusted by Industry Leaders</h3>
              
              <div 
                className="relative flex overflow-hidden"
                style={{
                  maskImage: "linear-gradient(to right, transparent, black 20%, black 80%, transparent)",
                  WebkitMaskImage: "linear-gradient(to right, transparent, black 20%, black 80%, transparent)"
                }}
              >
                <div className="animate-marquee flex gap-12 whitespace-nowrap px-4">
                  {/* Triple list for seamless loop */}
                  {[...CLIENTS, ...CLIENTS, ...CLIENTS].map((client, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 opacity-50 transition-all hover:opacity-100 hover:scale-105 cursor-default grayscale hover:grayscale-0"
                    >
                      {/* Brand Icon */}
                      <client.icon className="h-6 w-6 text-white" />
                      {/* Brand Name */}
                      <span className="text-lg font-bold text-white tracking-tight">
                        {client.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
